import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Trash2, Wand2, Truck, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { getFulfillmentSources, bulkManualDeliveryNotes, fmt } from "@/lib/api";
import { formatDate } from "@/lib/dates";

const rid = () => (crypto?.randomUUID?.() || `a${Date.now()}${Math.random()}`);
const keyOf = (pt, unit) => `${pt}|${unit}`;

/** Aggregate a client's orders into product/unit lines. */
function aggregateLines(orders) {
  const agg = {};
  orders.forEach((o) => (o.items || []).forEach((it) => {
    const k = keyOf(it.product_type, it.unit);
    agg[k] = agg[k] || { key: k, product_type: it.product_type, unit: it.unit, ordered: 0 };
    agg[k].ordered += Number(it.quantity);
  }));
  return Object.values(agg);
}

/**
 * FIFO auto-allocation for every client, in order, sharing one stock pool so the same lot is
 * never promised twice. `sources[key]` are sorted earliest-expiry first by the backend.
 */
function autoAllocate(clients, sources) {
  const used = {}; // source_id -> qty already taken by earlier clients
  return clients.map((c) => ({
    ...c,
    lines: c.lines.map((ln) => {
      let need = ln.ordered;
      const allocations = [];
      for (const s of sources[ln.key] || []) {
        if (need <= 1e-6) break;
        const avail = Number(s.remaining) - (used[s.id] || 0);
        if (avail <= 1e-6) continue;
        const take = Math.min(need, avail);
        allocations.push({ _rid: rid(), source_id: s.id, quantity: String(Number(take.toFixed(3))) });
        used[s.id] = (used[s.id] || 0) + take;
        need -= take;
      }
      if (allocations.length === 0) allocations.push({ _rid: rid(), source_id: "", quantity: String(ln.ordered) });
      return { ...ln, allocations };
    }),
  }));
}

export function BulkDeliverDialog({ orders, onClose, onDelivered }) {
  const { t, td } = useT();
  const [clients, setClients] = useState([]);      // [{client_id, client_name, orders, lines:[{...,allocations}]}]
  const [sources, setSources] = useState({});      // key -> [source]
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});  // client_id -> bool
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [confirmSL, setConfirmSL] = useState(null); // { indexes, message }
  const [errors, setErrors] = useState({});        // client_id -> reason

  // Build client groups + fetch every needed source list once.
  useEffect(() => {
    const byClient = {};
    orders.forEach((o) => {
      byClient[o.client_id] = byClient[o.client_id] || { client_id: o.client_id, client_name: o.client_name, orders: [] };
      byClient[o.client_id].orders.push(o);
    });
    const groups = Object.values(byClient).map((c) => ({ ...c, lines: aggregateLines(c.orders) }));
    const keys = [...new Set(groups.flatMap((c) => c.lines.map((l) => l.key)))];
    setLoading(true);
    Promise.all(keys.map((k) => { const [pt, unit] = k.split("|"); return getFulfillmentSources(pt, unit).catch(() => []); }))
      .then((results) => {
        const srcMap = {};
        keys.forEach((k, i) => { srcMap[k] = results[i] || []; });
        setSources(srcMap);
        setClients(autoAllocate(groups, srcMap));
      })
      .finally(() => setLoading(false));
  }, [orders]);

  // How much of each source is allocated across ALL clients (for shared-stock validation).
  const usedBySource = useMemo(() => {
    const used = {};
    clients.forEach((c) => c.lines.forEach((ln) => ln.allocations.forEach((a) => {
      if (a.source_id) used[a.source_id] = (used[a.source_id] || 0) + (Number(a.quantity) || 0);
    })));
    return used;
  }, [clients]);
  const sourceById = useMemo(() => {
    const m = {};
    Object.values(sources).forEach((arr) => arr.forEach((s) => { m[s.id] = s; }));
    return m;
  }, [sources]);

  const updateClient = (cid, fn) => setClients((x) => x.map((c) => (c.client_id === cid ? fn(c) : c)));
  const setAlloc = (cid, key, idx, patch) => updateClient(cid, (c) => ({
    ...c, lines: c.lines.map((ln) => (ln.key === key ? { ...ln, allocations: ln.allocations.map((a, i) => (i === idx ? { ...a, ...patch } : a)) } : ln)),
  }));
  const addAlloc = (cid, key) => updateClient(cid, (c) => ({
    ...c, lines: c.lines.map((ln) => (ln.key === key ? { ...ln, allocations: [...ln.allocations, { _rid: rid(), source_id: "", quantity: "" }] } : ln)),
  }));
  const rmAlloc = (cid, key, idx) => updateClient(cid, (c) => ({
    ...c, lines: c.lines.map((ln) => (ln.key === key ? { ...ln, allocations: ln.allocations.filter((_, i) => i !== idx) } : ln)),
  }));
  const removeClient = (cid) => setClients((x) => x.filter((c) => c.client_id !== cid));
  const reAuto = () => setClients((x) => autoAllocate(x.map((c) => ({ ...c, lines: aggregateLines(c.orders) })), sources));

  const allocatedFor = (ln) => ln.allocations.reduce((s, a) => s + (Number(a.quantity) || 0), 0);
  const lineState = (ln) => {
    const a = allocatedFor(ln);
    if (a > ln.ordered + 1e-6) return "over";
    if (a < ln.ordered - 1e-6) return "short";
    return "ok";
  };
  const sourceOver = (sid) => sid && sourceById[sid] && (usedBySource[sid] || 0) > Number(sourceById[sid].remaining) + 1e-6;
  const clientState = (c) => {
    const states = c.lines.map(lineState);
    const anyOver = states.includes("over") || c.lines.some((ln) => ln.allocations.some((a) => sourceOver(a.source_id)));
    if (anyOver) return "over";
    if (states.includes("short")) return "short";
    return "ok";
  };
  const stateBadge = { ok: "badge-ok", short: "badge-warn", over: "badge-bad" };
  const readyCount = clients.filter((c) => clientState(c) === "ok").length;

  const buildPayload = (only = null) => clients
    .filter((c, i) => !only || only.includes(i))
    .map((c) => ({
      order_ids: c.orders.map((o) => o.id),
      lines: c.lines.flatMap((ln) => ln.allocations
        .filter((a) => a.source_id && Number(a.quantity) > 0 && sourceById[a.source_id])
        .map((a) => ({
          product_type: ln.product_type, unit: ln.unit, quantity: Number(a.quantity),
          source_type: sourceById[a.source_id].source_type, source_id: a.source_id,
        }))),
    }));

  const submit = async (override = false, only = null) => {
    const payloadClients = buildPayload(only);
    if (payloadClients.length === 0 || payloadClients.every((c) => c.lines.length === 0)) {
      toast.error(t("orders.selectSourceQty"));
      return;
    }
    if (!override && clients.some((c) => clientState(c) === "over")) {
      toast.error(t("orders.bulkOverAllocated"));
      return;
    }
    setSaving(true);
    try {
      const res = await bulkManualDeliveryNotes({ clients: payloadClients, date, notes, override_min_shelf_life: override });
      const created = res.created || [];
      const skipped = res.skipped || [];
      if (created.length) toast.success(t("orders.bulkCreated", { n: created.length }));
      // Drop the clients that were delivered; keep failures visible with their reason.
      const createdClientIds = new Set(created.map((dn) => dn.client_id));
      const remaining = clients.filter((c) => !createdClientIds.has(c.client_id));
      const errs = {};
      const confirmIdx = [];
      skipped.forEach((sk) => {
        const c = remaining.find((x) => x.orders.some((o) => sk.order_ids?.includes(o.id)));
        if (!c) return;
        if (sk.code === "min_shelf_life_confirm") confirmIdx.push(remaining.indexOf(c));
        else errs[c.client_id] = sk.reason;
      });
      setClients(remaining);
      setErrors(errs);
      if (confirmIdx.length) {
        const msgs = skipped.filter((sk) => sk.code === "min_shelf_life_confirm").map((sk) => `${sk.client_name}: ${sk.reason}`);
        setConfirmSL({ indexes: confirmIdx, message: msgs.join("\n") });
      } else if (remaining.length === 0) {
        onDelivered(created.length);
      } else if (created.length) {
        onDelivered(created.length, true); // refresh list but keep dialog open for the failures
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : null) || t("orders.failedDn"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-4xl" data-testid="bulk-deliver-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Truck className="h-5 w-5 text-[hsl(var(--brand))]" /> {t("orders.bulkTitle")}</DialogTitle>
            <DialogDescription>{t("orders.bulkDesc", { clients: clients.length, orders: clients.reduce((s, c) => s + c.orders.length, 0) })}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{t("common.date")}</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-[160px]" data-testid="bulk-deliver-date" />
            </div>
            <div className="min-w-[220px] flex-1 space-y-1">
              <Label className="text-xs">{t("orders.bulkNotes")}</Label>
              <Textarea rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-9" data-testid="bulk-deliver-notes" />
            </div>
            <Button variant="outline" size="sm" onClick={reAuto} disabled={loading} data-testid="bulk-deliver-auto">
              <Wand2 className="mr-1.5 h-4 w-4" /> {t("orders.bulkAutoAllocate")}
            </Button>
          </div>

          <div className="max-h-[56vh] space-y-3 overflow-y-auto thin-scroll pr-1" data-testid="bulk-deliver-clients">
            {loading ? (
              Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)
            ) : clients.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t("orders.bulkNoClients")}</p>
            ) : clients.map((c) => {
              const st = clientState(c);
              const isCollapsed = !!collapsed[c.client_id];
              const Chevron = isCollapsed ? ChevronRight : ChevronDown;
              return (
                <div key={c.client_id} className="rounded-lg border border-border" data-testid={`bulk-client-${c.client_id}`}>
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => setCollapsed((x) => ({ ...x, [c.client_id]: !isCollapsed }))} data-testid={`bulk-client-toggle-${c.client_id}`}>
                      <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate font-medium">{c.client_name}</span>
                      <span className="truncate font-mono text-xs text-muted-foreground">{c.orders.map((o) => o.order_code).join(", ")}</span>
                    </button>
                    <Badge className={stateBadge[st]} data-testid={`bulk-client-state-${c.client_id}`}>
                      {st === "ok" ? <CheckCircle2 className="mr-1 h-3 w-3" /> : <AlertTriangle className="mr-1 h-3 w-3" />}
                      {t(`orders.bulkState_${st}`)}
                    </Badge>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title={t("orders.bulkRemoveClient")} onClick={() => removeClient(c.client_id)} data-testid={`bulk-client-remove-${c.client_id}`}>
                      <X className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                  {errors[c.client_id] && (
                    <p className="mx-3 mb-2 rounded-md bg-[hsl(var(--danger)/0.08)] px-2.5 py-1.5 text-xs text-[hsl(var(--danger))]" data-testid={`bulk-client-error-${c.client_id}`}>
                      {errors[c.client_id]}
                    </p>
                  )}
                  {!isCollapsed && (
                    <div className="space-y-2 border-t border-border p-3">
                      {c.lines.map((ln) => {
                        const opts = sources[ln.key] || [];
                        const allocated = allocatedFor(ln);
                        const ls = lineState(ln);
                        return (
                          <div key={ln.key} className="rounded-md bg-secondary/40 p-2.5" data-testid={`bulk-line-${c.client_id}-${ln.key}`}>
                            <div className="mb-1.5 flex items-center justify-between">
                              <span className="text-sm font-medium">{td(ln.product_type)} <span className="text-muted-foreground">({ln.unit})</span> <span className="text-xs text-muted-foreground">· {t("orders.ordered", { n: fmt(ln.ordered, 2) })}</span></span>
                              <Badge className={stateBadge[ls]}>{t("orders.allocated", { a: fmt(allocated, 2), o: fmt(ln.ordered, 2) })}</Badge>
                            </div>
                            <div className="space-y-1.5">
                              {ln.allocations.map((a, idx) => {
                                const s = sourceById[a.source_id];
                                const over = sourceOver(a.source_id);
                                return (
                                  <div key={a._rid} className="grid grid-cols-12 items-center gap-2">
                                    <div className="col-span-7">
                                      <Select value={a.source_id} onValueChange={(v) => setAlloc(c.client_id, ln.key, idx, { source_id: v })}>
                                        <SelectTrigger className={over ? "border-[hsl(var(--danger))]" : ""} data-testid={`bulk-source-${c.client_id}-${ln.key}-${idx}`}>
                                          <SelectValue placeholder={opts.length ? t("orders.selectSource") : t("orders.noStock")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {opts.map((o) => {
                                            const others = (usedBySource[o.id] || 0) - (o.id === a.source_id ? (Number(a.quantity) || 0) : 0);
                                            const left = Number(o.remaining) - others;
                                            return (
                                              <SelectItem key={o.id} value={o.id}>
                                                {o.code} · {t("orders.sourceLeft", { n: fmt(Math.max(left, 0), 2) })}{others > 0 ? ` (${t("orders.bulkUsedElsewhere", { n: fmt(others, 2) })})` : ""}{o.expiry_date ? ` · ${t("orders.expShort")} ${formatDate(o.expiry_date)}` : ""}{o.source_type === "production" ? ` ${t("orders.batchTag")}` : ""}
                                              </SelectItem>
                                            );
                                          })}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="col-span-4">
                                      <Input type="number" className={`text-right font-mono ${over ? "border-[hsl(var(--danger))]" : ""}`} value={a.quantity} placeholder={t("common.qty")} onChange={(e) => setAlloc(c.client_id, ln.key, idx, { quantity: e.target.value })} data-testid={`bulk-qty-${c.client_id}-${ln.key}-${idx}`} />
                                    </div>
                                    <div className="col-span-1">
                                      {ln.allocations.length > 1 && (
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => rmAlloc(c.client_id, ln.key, idx)} data-testid={`bulk-alloc-remove-${c.client_id}-${ln.key}-${idx}`}>
                                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                                        </Button>
                                      )}
                                    </div>
                                    {over && s && (
                                      <p className="col-span-12 text-[11px] text-[hsl(var(--danger))]">{t("orders.bulkLotOver", { code: s.code, n: fmt(s.remaining, 2) })}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <Button variant="ghost" size="sm" className="mt-1 h-7 px-2 text-xs" onClick={() => addAlloc(c.client_id, ln.key)} disabled={opts.length <= 1} data-testid={`bulk-add-lot-${c.client_id}-${ln.key}`}>
                              <Plus className="mr-1 h-3.5 w-3.5" /> {t("orders.addAnotherLot")}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <DialogFooter className="items-center gap-2 sm:justify-between">
            <span className="text-xs text-muted-foreground" data-testid="bulk-deliver-ready">{t("orders.bulkReady", { ready: readyCount, total: clients.length })}</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
              <Button onClick={() => submit()} disabled={saving || loading || clients.length === 0} data-testid="bulk-deliver-submit">
                <Truck className="mr-1.5 h-4 w-4" /> {saving ? t("common.saving") : t("orders.bulkSubmit", { n: clients.length })}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmSL} onOpenChange={(o) => !o && setConfirmSL(null)}>
        <AlertDialogContent data-testid="bulk-shelf-life-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("orders.shortShelfLifeTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">{confirmSL?.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const idx = confirmSL.indexes; setConfirmSL(null); submit(true, idx); }} data-testid="bulk-shelf-life-confirm-btn">
              {t("orders.sellAnyway")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
